import { Box, Button, Grid, Modal, Paper, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import theme from "../../../theme/theme";

import { addScheduleApi } from "../../../API/schedule";
import { getUsersApi, getUsersByCompanyApi } from "../../../API/users";
import { errorResponseHandler, successResponseHandler } from "../../../utils/reposeAlert";
import MeetingDatePicker from "../../Meeting/MeetingDatePicker";
import { validateMeetingTimes } from "../../../utils/meetingStatus";
import MeetingInput from "../../Meeting/MeetingInput";
import MeetingMultipleSelection from "../../Meeting/MeetingMultipleSelection";
import MeetingSingleSelection from "../../Meeting/MeetingSingleSelection";
import MeetingTextarea from "../../Meeting/MeetingTextarea";
import SharedButton from "../../Shared/SharedButton";
import { toWallClockDate } from "../../../utils/formateDate";

const AddMeetingModal = ({ openMeetingModal, toggleMeetingModalHandler, getMeetings }) => {
  const [title, setTitle] = useState("");
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [startTimeToSubmit, setStartTimeToSubmit] = useState(null);
  const [endTimeToSubmit, setEndTimeToSubmit] = useState(null);
  const [description, setDescription] = useState("");
  const [selectedCompanies, setSelectedCompanies] = useState(null);
  const [selectedInvitees, setSelectedInvitees] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [inviteeOptions, setInviteeOptions] = useState([]);
  const [disabledAddMeeting, setDisabledAddMeeting] = useState(false);

  const handleStartTimeChange = (date) => {
    // Send the picked wall-clock as UTC digits (matches backend/mobile).
    setStartTimeToSubmit(toWallClockDate(date));
    setStartTime(date);
    const endDate = new Date(date);
    endDate.setHours(endDate.getHours() + 1);
    setEndTimeToSubmit(toWallClockDate(endDate));
    setEndTime(endDate);
    setSelectedCompanies(null);
    setSelectedInvitees([]);
  };
  const handleEndTimeChange = (date) => {
    setEndTimeToSubmit(toWallClockDate(date));
    setEndTime(date);
    setSelectedCompanies(null);
    setSelectedInvitees([]);
  };
  
  const handleAddMeeting = () => {};
  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: {
      xs: "100%",
      md: "45rem",
    },
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 2,
  };

  useEffect(() => {
    getCompanies();
  }, []);

  const getCompanies = async () => {
    await getUsersApi()
      .then((response) => {
        if (response.status === 200) {
          let companies = [];

          // eslint-disable-next-line array-callback-return
          response.data.data.map((item) => {
            if (item.company && companies.findIndex((x) => x.id === item.company.id) === -1) {
              companies.push({ name: item.company.name, id: item.company.id });
            }
          });
          // Sort company names alphabetically (case-insensitive) for the dropdown.
          companies.sort((a, b) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          );
          setCompanyOptions(companies);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally({});
  };

  const companySelectHandler = async (company) => {
    if (!company || !startTime || !endTime) {
      setSelectedCompanies(null);
      setSelectedInvitees([]);
      setInviteeOptions([]);
      return;
    }
    setSelectedCompanies(company);
    setSelectedInvitees([]);
    await getUsersByCompanyApi(company.id, startTimeToSubmit.toISOString(), endTimeToSubmit.toISOString())
      .then((response) => {
        if (response.status === 200) {
          let invitees = [];
          const users = response.data?.data ?? response.data;

          // eslint-disable-next-line array-callback-return
          users.map((item, index) => {
            invitees.push({
              name: `${item.firstName} ${item.lastName} - (${item.position})`,
              id: item.id,
              status: item?.status || "available",
            });
          });
          setInviteeOptions(invitees);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally({});
  };
  const submitHandler = async (e) => {
    e.preventDefault();

    // Validate the picked times before hitting the API (item 1/2/3): 9 AM–4:45 PM
    // start, end by 5 PM, and start/end on the same day. Shows a clear alert.
    const timeError = validateMeetingTimes(startTime, endTime);
    if (timeError) {
      errorResponseHandler("Invalid Time", timeError);
      return;
    }

    setDisabledAddMeeting(true);
    if (!disabledAddMeeting) {
      const addScheduleData = {
        title,
        description,
        startDate: startTimeToSubmit.toISOString(),
        endDate: endTimeToSubmit.toISOString(),
        inviteeIds: selectedInvitees.map((invite) => {
          return invite.id;
        }),
      };
      await addScheduleApi(addScheduleData)
        .then((response) => {
          if (response.status === 200 || response.status === 201) {
            toggleMeetingModalHandler(false);
            successResponseHandler(`Schedule Created`, response.data.message);
            resetAddScheduleFields();
            getMeetings();
          }
        })
        .catch((error) => {
          const msg = error.response.data;
          errorResponseHandler(`Failed`, msg);
        })
        .finally(() => {
          setDisabledAddMeeting(false);
        });
    }
  };

  const resetAddScheduleFields = () => {
    setTitle("");
    setStartTime(null);
    setEndTime(null);
    setDescription("");
    setSelectedCompanies(null);
    setSelectedInvitees([]);
    setInviteeOptions([]);
  };

  return (
    <Modal
      open={openMeetingModal}
      onClose={() => toggleMeetingModalHandler(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        zIndex: 10,
      }}
    >
      <Paper elevation={3} sx={modalStyle}>
        <Typography
          variant="h4"
          sx={{
            color: theme.palette.primary.main,
            fontWeight: "bold",
            marginBottom: "2rem",
          }}
        >
          Add Meeting
        </Typography>
        <form onSubmit={submitHandler}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MeetingInput label="Meeting title" title={title} setTitle={setTitle} />
            </Grid>
            <Grid item xs={12} md={6}>
              <MeetingDatePicker label="Start Time" handleChange={handleStartTimeChange} value={startTime} />
            </Grid>
            <Grid item xs={12} md={6}>
              <MeetingDatePicker
                label="End Time"
                handleChange={handleEndTimeChange}
                value={endTime}
                // Lock the end date to the start's day so a meeting can't span
                // two days (item 2). Falls back to the full window if no start.
                minDate={startTime ? new Date(new Date(startTime).setHours(0, 0, 0, 0)) : undefined}
                maxDate={startTime ? new Date(new Date(startTime).setHours(23, 59, 59, 0)) : undefined}
              />
            </Grid>
            <Grid item xs={12}>
              <MeetingTextarea description={description} setDescription={setDescription} />
            </Grid>
            {companyOptions.length ? (
              <Grid item xs={12} md={12}>
                <MeetingSingleSelection
                  options={companyOptions}
                  selected={selectedCompanies}
                  setSelected={companySelectHandler}
                  disabled={!startTime || !endTime}
                />
              </Grid>
            ) : null}

            <Grid item xs={12} md={12}>
              <MeetingMultipleSelection
                options={inviteeOptions}
                selected={selectedInvitees}
                setSelected={setSelectedInvitees}
                disabled={!selectedCompanies}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  borderTop: `1px solid ${theme.palette.primary.main}`,
                  paddingTop: "1rem",
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Button variant="outlined" color="primary" onClick={() => toggleMeetingModalHandler(false)}>
                  Cancel
                </Button>

                <SharedButton
                  color="primary"
                  text="Save"
                  onAction={handleAddMeeting}
                  disabled={disabledAddMeeting}
                  type="submit  "
                  
                />
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Modal>
  );
};

export default AddMeetingModal;